import * as Sentry from '@sentry/browser'
import { get, noop } from 'lodash'
import moment from 'moment'
import { flashNotification } from 'src/actions'
import { fetchValuationHistory } from 'src/actions/valuationHistory'
import { ERRORS, NO_ACCESS_ERROR } from 'src/constants/messages'
import {
  SET_HOME_FETCH_ID,
  SET_HOME_FROM_CACHE,
  SET_CLIENT_FETCH_ID,
  FETCH_HOME_REQUEST,
  FETCH_HOME_SUCCESS,
  FETCH_HOME_FAILURE,
  UPDATE_HOME_REQUEST,
  UPDATE_HOME_SUCCESS,
  UPDATE_HOME_ERROR,
  ADD_HOME_ANSWER_REQUEST,
  ADD_HOME_ANSWER_SUCCESS,
  ADD_HOME_ANSWER_ERROR,
  CREATE_HOME_LOAN_REQUEST,
  CREATE_HOME_LOAN_SUCCESS,
  CREATE_HOME_LOAN_FAILURE,
  DELETE_LOAN_REQUEST,
  DELETE_LOAN_SUCCESS,
  DELETE_LOAN_FAILURE,
  UPDATE_LOAN_REQUEST,
  UPDATE_LOAN_SUCCESS,
  UPDATE_LOAN_FAILURE,
  UPDATE_LOAN_BALANCES_REQUEST,
  UPDATE_LOAN_BALANCES_SUCCESS,
  UPDATE_LOAN_BALANCES_FAILURE,
  UPDATE_LOAN_BALANCES_SAVED_RESET,
  HOME_DIGEST_UNSUBSCRIBE,
  HOME_DIGEST_SUBSCRIBE,
  HOME_EMAIL_REQUEST,
  HOME_EMAIL_SUCCESS,
  HOME_EMAIL_FAILURE
} from './actionTypes'
import { LOAN_STATUS } from 'src/constants/loan'

import {
  getHomeDetail as requestGetHomeDetail,
  legacyCreateLoan,
  legacyUpdateLoan,
  updateHome as requestUpdateHome,
  addHomeAnswer as requestAddHomeAnswer,
  unsubscribe as sendUnsubscribe,
  subscribe as sendSubscribe,
  trackActivity,
  requestEmail
} from 'src/api/node/requests'
import {
  submitLoanBalance,
  trackClientHomeEvent
} from 'src/api/mikasa/requests'

import { getAuthEmail, hasCustomerToken } from 'src/auth'
import {
  selectHomeId,
  selectHomeData,
  selectCurrentHomeUser,
  getHomeUserFromEmail
} from 'src/store/selectors/home'
import { selectCustomerLegacyRef } from 'src/store/selectors/customerProfile'
import { selectClientFetchId, selectClientId } from 'src/store/selectors/client'
import { selectStreamToken } from 'src/store/selectors/home/homeowner'
import { getHomeNotificationsIfNeeded } from './streamNotifications'
import { selectAuthedAsClient } from 'src/store/selectors/auth'

export const fetchHomeDetail = homeId => async (dispatch, getState) => {
  const { cache } = getState().home
  const isCached = Boolean(cache[homeId])

  dispatch({
    type: FETCH_HOME_REQUEST,
    id: homeId,
    loading: !isCached
  })

  try {
    const response = await requestGetHomeDetail(homeId)

    dispatch({
      type: FETCH_HOME_SUCCESS,
      data: response.data
    })

    dispatch(fetchValuationHistory(response.data.uuid))

    // Home data to set clientId for old home routes
    const clientFetchId = selectClientFetchId(getState())
    if (clientFetchId === null) {
      dispatch({
        type: SET_CLIENT_FETCH_ID,
        id: getHomeUserFromEmail(response.data, getAuthEmail()).uuid
      })
    }

    const token = selectStreamToken(getState())
    if (token) {
      dispatch(getHomeNotificationsIfNeeded(token))
    }
  } catch (error) {
    dispatch({
      type: FETCH_HOME_FAILURE,
      error
    })
  }
}

export const setCurrentHomeDetail = homeId => async (dispatch, getState) => {
  const { cache } = getState().home
  const isCached = Boolean(cache[homeId])

  dispatch({
    type: homeId && isCached ? SET_HOME_FROM_CACHE : SET_HOME_FETCH_ID,
    id: homeId,
    keepCache: true
  })
}

export const updateHome = data => async (dispatch, getState) => {
  dispatch({
    type: UPDATE_HOME_REQUEST
  })

  const { id } = getState().home.data

  try {
    const response = await requestUpdateHome(id, data)
    dispatch({
      type: UPDATE_HOME_SUCCESS,
      data: response.data
    })
  } catch (error) {
    dispatch({
      type: UPDATE_HOME_ERROR,
      error
    })
  }
}

export const addHomeAnswer = (answerData, withHomeUpdate = true) => async (
  dispatch,
  getState
) => {
  dispatch({
    type: ADD_HOME_ANSWER_REQUEST
  })

  const { id } = getState().home.data

  try {
    const response = await requestAddHomeAnswer(id, answerData)
    dispatch({
      type: ADD_HOME_ANSWER_SUCCESS,
      data: {
        updatedData: response.data,
        answerData
      }
    })

    if (withHomeUpdate) {
      dispatch(fetchHomeDetail(id))
    }
  } catch (error) {
    dispatch({
      type: ADD_HOME_ANSWER_ERROR,
      error
    })
  }
}

export const createHomeLoan = (homeId, data) => async dispatch => {
  dispatch({
    type: CREATE_HOME_LOAN_REQUEST
  })

  try {
    const response = await legacyCreateLoan(homeId, data)
    dispatch({
      type: CREATE_HOME_LOAN_SUCCESS,
      data: response.data
    })

    dispatch(fetchHomeDetail(homeId))
  } catch (error) {
    dispatch({
      type: CREATE_HOME_LOAN_FAILURE,
      error
    })
  }
}

export const updateLoanBalances = loanAmountMap => async (
  dispatch,
  getState
) => {
  dispatch({
    type: UPDATE_LOAN_BALANCES_REQUEST
  })
  try {
    const now = moment().toJSON()
    const promises = Object.keys(loanAmountMap).map(key => {
      return submitLoanBalance(key, loanAmountMap[key], now)
    })

    await Promise.all(promises).then(responses => {
      dispatch({
        type: UPDATE_LOAN_BALANCES_SUCCESS,
        data: responses
      })
    })

    const state = getState()
    dispatch(fetchHomeDetail(selectHomeId(state)))
  } catch (error) {
    dispatch({
      type: UPDATE_LOAN_BALANCES_FAILURE,
      error
    })
    if (error.message.includes('403')) {
      dispatch(
        flashNotification({
          message: NO_ACCESS_ERROR.defaultMessage,
          messageType: 'error'
        })
      )
    } else {
      dispatch(
        flashNotification({
          message: [ERRORS.defaultHeadline, ERRORS.defaultMessage],
          messageType: 'error'
        })
      )
    }
  }
}

export const resetLoanBalancedSaved = () => dispatch => {
  dispatch({ type: UPDATE_LOAN_BALANCES_SAVED_RESET })
}

export const deleteHomeLoan = data => async (dispatch, getState) => {
  const { id: loanId, homeId } = getState().loan.currentLoan
  const data = { status: LOAN_STATUS.DELETED }

  dispatch({
    type: DELETE_LOAN_REQUEST
  })

  try {
    const response = await legacyUpdateLoan(loanId, data)
    dispatch({
      type: DELETE_LOAN_SUCCESS,
      data: response.data
    })

    dispatch(fetchHomeDetail(homeId))
  } catch (error) {
    dispatch({
      type: DELETE_LOAN_FAILURE,
      error
    })
  }
}

export const updateHomeLoan = data => async dispatch => {
  const { id: loanId, homeId, ...loanProperties } = data

  dispatch({
    type: UPDATE_LOAN_REQUEST
  })

  try {
    const response = await legacyUpdateLoan(loanId, loanProperties)
    dispatch({
      type: UPDATE_LOAN_SUCCESS,
      data: response.data
    })

    dispatch(fetchHomeDetail(homeId))
  } catch (error) {
    dispatch({
      type: UPDATE_LOAN_FAILURE,
      error
    })
  }
}

export const toggleSubscription = ({
  onSuccess = noop,
  onError = noop
}) => async (dispatch, getState) => {
  const state = getState()
  const id = selectCurrentHomeUser(state).id
  const isUnsubscribed = get(state, 'home.isUnsubscribed')

  const method = isUnsubscribed ? sendSubscribe : sendUnsubscribe
  const type = isUnsubscribed ? HOME_DIGEST_SUBSCRIBE : HOME_DIGEST_UNSUBSCRIBE

  try {
    await method(id)
    dispatch({ type })
    onSuccess()
  } catch (e) {
    onError(e)
  }
}

const isCMARequest = params => get(params, 'event') === 'cma-request'

export const trackHomeActivityMikasa = params => async (dispatch, getState) => {
  const state = getState()
  const home = selectHomeData(state)
  const clientId = selectClientId(state)

  const homeId = get(home, 'uuid')

  const eventName = get(params, 'eventName')
  const eventProperties = get(params, 'eventProperties')

  const authedAsCustomer = !selectAuthedAsClient(state)

  if (!clientId || !homeId || !eventName || authedAsCustomer) {
    return
  }

  try {
    await trackClientHomeEvent(eventName, clientId, homeId, eventProperties)
  } catch (e) {
    Sentry.captureException(e, {
      extra: {
        clientId: clientId,
        homeId: homeId,
        eventName: eventName,
        eventProperties: eventProperties
      }
    })
  }
}

export const trackHomeActivity = params => async (dispatch, getState) => {
  const state = getState()
  const home = selectHomeData(state)
  const agentId = selectCustomerLegacyRef(state) || {}
  const user = selectCurrentHomeUser(state)

  if (!home || !user || (hasCustomerToken() && !isCMARequest(params))) {
    return
  }

  try {
    await trackActivity({
      ...params,
      agentId,
      homeId: home.id,
      clientId: user.id
    })
  } catch (e) {
    Sentry.captureException(e, {
      extra: {
        agentId,
        clientId: user.id,
        isAgent: hasCustomerToken()
      }
    })
  }
}

export const trackRefiSliderInteraction = () => dispatch => {
  dispatch(
    trackHomeActivityMikasa({
      eventName: 'refi-slider-interaction',
      eventProperties: {
        activity_source: 'user'
      }
    })
  )
}

export const trackInstantOfferRequested = () => dispatch => {
  dispatch(
    trackHomeActivityMikasa({
      eventName: 'instant-offer-requested',
      eventProperties: {
        activity_source: 'user'
      }
    })
  )
}

export const trackCMARequest = () => dispatch =>
  dispatch(trackHomeActivity({ event: 'cma-request' }))

export const requestHomeEmail = (homeId, email) => async dispatch => {
  dispatch({
    type: HOME_EMAIL_REQUEST
  })

  try {
    await requestEmail(homeId, email)
    dispatch({
      type: HOME_EMAIL_SUCCESS
    })
  } catch (e) {
    dispatch({
      type: HOME_EMAIL_FAILURE
    })
  }
}
